import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import CartDrawer from '@/components/CartDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback parallax images if admin hasn't uploaded a video yet
// Hero fallback is now a pure radial-gradient (see JSX below). The old Pexels URLs
// used to flash briefly before the user's uploaded video/photo loaded — deleted.

const HomePage = () => {
  const navigate = useNavigate();
  const [cartOpen, setCartOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const heroRef = useRef(null);
  const deepRef = useRef(null);
  const midRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => setSettings(data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    let rafId = null;
    let targetX = 0, targetY = 0;
    let currentX = 0, currentY = 0;

    const onMove = (e) => {
      const rect = hero.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      targetY = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      if (!rafId) rafId = requestAnimationFrame(tick);
    };

    const tick = () => {
      currentX += (targetX - currentX) * 0.08;
      currentY += (targetY - currentY) * 0.08;
      if (deepRef.current) {
        deepRef.current.style.transform =
          `translate3d(${(-currentX * 14).toFixed(2)}px, ${(-currentY * 10).toFixed(2)}px, 0) scale(1.06)`;
      }
      if (midRef.current) {
        midRef.current.style.transform =
          `translate3d(${(-currentX * 32).toFixed(2)}px, ${(-currentY * 22).toFixed(2)}px, 0) scale(1.08)`;
      }
      if (Math.abs(targetX - currentX) > 0.001 || Math.abs(targetY - currentY) > 0.001) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };

    hero.addEventListener('mousemove', onMove);
    return () => {
      hero.removeEventListener('mousemove', onMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const hasVideo = Boolean(settings.hero_video_url);
  const poster = settings.hero_poster_url;

  return (
    <div className="min-h-screen flex flex-col bg-[#09090B] text-white">
      <SiteHeader onCartClick={() => setCartOpen(true)} />

      <main className="flex-1 flex">
        <section
          ref={heroRef}
          className="relative w-full flex items-center justify-center overflow-hidden"
          style={{ minHeight: 'calc(100vh - 64px - 65px)' }}
          data-testid="hero-section"
        >
          {hasVideo ? (
            <>
              {/* Video background with parallax wrapper (deepRef) */}
              <div
                ref={deepRef}
                className="parallax-layer"
                data-testid="parallax-deep"
              >
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={poster || undefined}
                  src={settings.hero_video_url}
                  data-testid="hero-video"
                />
              </div>
              {/* Mid parallax layer as a soft light glow only */}
              <div
                ref={midRef}
                className="parallax-layer"
                style={{
                  background:
                    'radial-gradient(ellipse at 30% 40%, rgba(0,240,255,0.10) 0%, rgba(0,240,255,0) 55%), radial-gradient(ellipse at 70% 60%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 60%)',
                }}
                data-testid="parallax-mid"
              />
            </>
          ) : (
            <>
              {/* No hero video uploaded yet — solid dark background instead of a
                  stock studio photo (which used to flash as a glitch before the
                  user's uploaded video loaded). */}
              <div
                ref={deepRef}
                className="parallax-layer"
                style={{ background: 'radial-gradient(ellipse at 50% 40%, #18181B 0%, #09090B 60%, #000000 100%)' }}
                data-testid="parallax-deep"
              />
              <div
                ref={midRef}
                className="parallax-layer"
                style={{
                  background: 'radial-gradient(ellipse at 30% 40%, rgba(0,240,255,0.10) 0%, rgba(0,240,255,0) 55%), radial-gradient(ellipse at 70% 60%, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 60%)',
                }}
                data-testid="parallax-mid"
              />
            </>
          )}

          {/* Dark gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/85 via-[#09090B]/55 to-[#09090B]/95 pointer-events-none" />

          {/* Grain */}
          <div
            className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
            style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22/></filter><rect width=%22100%22 height=%22100%22 filter=%22url(%23n)%22/></svg>")' }}
          />

          {/* Subtle sweeping light beams for "alive" feel */}
          <div className="hero-light-beam hero-light-beam-a pointer-events-none" />
          <div className="hero-light-beam hero-light-beam-b pointer-events-none" />

          {/* Content */}
          <div className="relative z-10 text-center px-6 max-w-6xl w-full">
            <h1
              className="font-heading tracking-tight mb-6 hero-chrome md:whitespace-nowrap hero-glow"
              data-testid="hero-title"
              style={{
                fontSize: 'clamp(1.75rem, 6.2vw, 5rem)',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                lineHeight: 1.05,
              }}
            >
              Welcome to Kehele Keff Music
            </h1>

            <p
              className="text-white/70 mb-10 tracking-wide"
              style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}
              data-testid="hero-tagline"
            >
              Professional Edits Made Affordable For Every DJ
            </p>

            <div className="btn-3d-wrapper inline-block">
              <button
                onClick={() => navigate('/collections')}
                className="btn-3d px-10 py-4 rounded-full text-sm font-semibold tracking-wide uppercase"
                data-testid="hero-shop-now-button"
              >
                Shop Now
              </button>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
};

export default HomePage;
